import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheckIcon,
  GlobeEuropeAfricaIcon,
  CircleStackIcon,
  DocumentMagnifyingGlassIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { t } from '../i18n';

const Feature = ({ icon: Icon, title, desc }) => (
  <div className="flex flex-col sm:flex-row gap-5 p-6 sm:p-8 bg-white border border-[#adc6ce]/30 rounded-2xl hover:border-[#155e75]/40 transition-all duration-300 group shadow-sm hover:shadow-md">
    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#f6f6f6] rounded-xl flex items-center justify-center text-[#155e75] group-hover:bg-[#155e75] group-hover:text-white transition-all duration-300 shadow-inner flex-shrink-0">
      <Icon className="w-6 h-6 sm:w-7 sm:h-7" />
    </div>
    <div className="flex-1">
      <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-2">{title}</h3>
      <p className="text-xs sm:text-sm text-slate-500 leading-relaxed font-medium">{desc}</p>
    </div>
  </div>
);

export default function Landing() {
  const [stats, setStats] = useState({
    mwh_issued: 0,
    active_plants: 0,
    participants: 0,
  });
  useEffect(() => {
    axios.get('/api/v1/certificates/summary/stats')
      .then(res => setStats(res.data))
      .catch(err => console.error('Stats fetch failed', err));
  }, []);

  const gwhValue = stats?.mwh_issued ? (stats.mwh_issued / 1000).toFixed(1) : '0.0';

  const features = [
    { icon: ShieldCheckIcon, titleKey: 'landing_feat_provenance_title', descKey: 'landing_feat_provenance_desc' },
    { icon: GlobeEuropeAfricaIcon, titleKey: 'landing_feat_interop_title', descKey: 'landing_feat_interop_desc' },
    { icon: CircleStackIcon, titleKey: 'landing_feat_liquidity_title', descKey: 'landing_feat_liquidity_desc' },
    { icon: DocumentMagnifyingGlassIcon, titleKey: 'landing_feat_oversight_title', descKey: 'landing_feat_oversight_desc' },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-800 font-sans">

      {/* Hero */}
      <section className="bg-white border-b border-gray-100 py-16 sm:py-24 lg:py-32 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(#155e75 1.5px, transparent 1.5px)', backgroundSize: '24px 24px' }}
        />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">

            <div className="lg:col-span-7 space-y-6 sm:space-y-8 text-center lg:text-left">

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.05] max-w-2xl mx-auto lg:mx-0">
                {t('landing_hero_before')}{' '}
                <span className="text-[#155e75] italic font-serif font-semibold">{t('landing_hero_highlight')}</span>
                {t('landing_hero_after') ? ` ${t('landing_hero_after')}` : ''}
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-slate-500 max-w-xl mx-auto lg:mx-0 font-medium leading-relaxed">
                {t('landing_hero_subtitle')}
              </p>

              <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
                <button
                  type="button"
                  onClick={() => { window.location.href = '/login'; }}
                  className="bg-[#155e75] text-white px-8 py-4 rounded-xl font-bold uppercase tracking-wider text-xs shadow-lg shadow-[#155e75]/15 hover:bg-[#0e4e61] transition-all duration-300 flex items-center justify-center gap-2"
                >
                  {t('landing_enter_registry')} <ArrowRightIcon className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => { window.location.href = '/register-type'; }}
                  className="bg-white border border-[#adc6ce]/60 text-slate-700 px-8 py-4 rounded-xl font-bold uppercase tracking-wider text-xs hover:border-[#155e75] hover:text-[#155e75] transition-all duration-300"
                >
                  {t('landing_register_account')}
                </button>
              </div>
            </div>

            <div className="hidden md:block lg:col-span-5 w-full max-w-md lg:max-w-none mx-auto">
              <div className="relative">
                <div className="absolute -inset-2 border border-slate-200/50 rounded-2xl rotate-2 opacity-60 pointer-events-none" />
                <div className="absolute -inset-2 border border-[#adc6ce]/40 rounded-2xl -rotate-2 opacity-40 pointer-events-none" />
                <div className="bg-white border-4 border-slate-50 rounded-2xl p-1.5 shadow-2xl relative overflow-hidden">
                  <img
                    src="/registry_dashboard.png"
                    alt={t('landing_registry_alt')}
                    className="w-full h-auto rounded-xl object-cover shadow-sm"
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 sm:py-20 border-b border-gray-100 bg-[#f6f6f6]/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="bg-white p-6 sm:p-8 rounded-2xl border border-[#adc6ce]/20 shadow-sm flex flex-col justify-between">
              <div>
                <p className="text-[10px] font-bold text-[#6a99a7] uppercase tracking-widest mb-3">{t('landing_stat_volume')}</p>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight tabular-nums">
                  {gwhValue} <span className="text-base font-bold text-slate-400 uppercase">GWh</span>
                </h2>
              </div>
              <div className="w-12 h-1 bg-[#155e75] rounded-full mt-4" />
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-2xl border border-[#adc6ce]/20 shadow-sm flex flex-col justify-between">
              <div>
                <p className="text-[10px] font-bold text-[#6a99a7] uppercase tracking-widest mb-3">{t('landing_stat_plants')}</p>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight tabular-nums">
                  {stats.active_plants} <span className="text-base font-bold text-slate-400 uppercase">{t('landing_unit_plants')}</span>
                </h2>
              </div>
              <div className="w-12 h-1 bg-indigo-500 rounded-full mt-4" />
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-2xl border border-[#adc6ce]/20 shadow-sm col-span-1 sm:col-span-2 lg:col-span-1 flex flex-col justify-between">
              <div>
                <p className="text-[10px] font-bold text-[#6a99a7] uppercase tracking-widest mb-3">{t('landing_stat_participants')}</p>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight tabular-nums">
                  {stats.participants} <span className="text-base font-bold text-slate-400 uppercase">{t('landing_unit_entities')}</span>
                </h2>
              </div>
              <div className="w-12 h-1 bg-emerald-500 rounded-full mt-4" />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="mb-12 sm:mb-16 text-center lg:text-left">
            <span className="text-xs font-bold tracking-widest text-[#155e75] uppercase block mb-2">{t('landing_protocol_badge')}</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight uppercase">{t('landing_governance_title')}</h2>
            <div className="w-12 h-1 bg-[#155e75] rounded-full mt-3 mx-auto lg:mx-0" />
            <p className="text-slate-500 text-sm font-medium italic mt-4">{t('landing_eecs_subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {features.map(({ icon, titleKey, descKey }) => (
              <Feature
                key={titleKey}
                icon={icon}
                title={t(titleKey)}
                desc={t(descKey)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-10 sm:py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-6 sm:gap-8">

          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#155e75] rounded-lg shadow-md flex items-center justify-center overflow-hidden">
              <img src="/go-logo.svg" alt="MAP GO" className="w-6 h-6" />
            </div>
            <span className="text-lg font-bold text-slate-900 tracking-tight">
              MAP <span className="text-[#155e75] font-light italic">GO</span> <span className="text-[#6a99a7] font-light ml-1">REGISTRY</span>
            </span>
          </div>

          <p className="text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider text-center sm:text-right flex flex-col sm:items-end gap-1">
            <Link to="/wikihow" className="text-[#155e75] hover:underline font-bold mb-1">{t('landing_wikihow_link')}</Link>
            <span>
              © 2026{' '}
              <a href="https://solutions.mapcure.com" target="_blank" rel="noreferrer" className="text-[#155e75] hover:underline font-bold">MAP</a>
              . {t('landing_copyright')}
            </span>
          </p>
        </div>
      </footer>

    </div>
  );
}
